import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, RotateCcw, Power, PlayCircle, StopCircle, Activity, List, Search, Maximize2, Server, PlusCircle, Trash2 } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import nebiusIcon from '../assets/icons/NBIS.svg';

const POLL_INTERVAL_OPTIONS = [
  { value: 60, label: '60 seconds' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' },
];

const REGION_OPTIONS = [
  { value: 'eu-north1', label: 'eu-north1 — Finland' },
  { value: 'eu-north2', label: 'eu-north2 — Finland' },
  { value: 'eu-west1', label: 'eu-west1 — France' },
  { value: 'me-west1', label: 'me-west1 — Israel' },
  { value: 'us-central1', label: 'us-central1 — US (Kansas City)' },
  { value: 'uk-south1', label: 'uk-south1 — UK' },
];

const formatPollInterval = (seconds?: number): string => {
  if (!seconds) return '5 minutes';
  if (seconds % 60 === 0) {
    const mins = seconds / 60;
    return mins === 1 ? '1 minute' : `${mins} minutes`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
};

const NebiusIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleProject, setExampleProject] = useState('');
  const [exampleDeveloper, setExampleDeveloper] = useState('');
  const [pollInterval, setPollInterval] = useState(300);
  const [connectedRegion, setConnectedRegion] = useState('eu-north1');

  const [credentials, setCredentials] = useState('');
  const [connectPollInterval, setConnectPollInterval] = useState(300);
  const [region, setRegion] = useState('eu-north1');
  const [credentialTab, setCredentialTab] = useState<'cli' | 'ui'>('cli');
  const [saServiceAccountId, setSaServiceAccountId] = useState('');
  const [saPublicKeyId, setSaPublicKeyId] = useState('');
  const [saPrivateKey, setSaPrivateKey] = useState('');

  const consoleFieldsFilled = !!(saServiceAccountId.trim() && saPublicKeyId.trim() && saPrivateKey.trim());
  const canConnect = credentialTab === 'cli' ? !!credentials.trim() : consoleFieldsFilled;

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getNebiusStatus();
      if (data?.connected) {
        setConnected(true);
        setPollInterval(data.poll_interval || 300);
        setConnectedRegion(data.region || 'eu-north1');
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      const base = { poll_interval: connectPollInterval, region: region.trim() || 'eu-north1' };
      if (credentialTab === 'cli') {
        await apiClient.connectNebius({ credentials, ...base });
      } else {
        await apiClient.connectNebius({
          service_account_id: saServiceAccountId.trim(),
          public_key_id: saPublicKeyId.trim(),
          private_key: saPrivateKey.trim(),
          ...base,
        });
      }
      setSuccessMsg('Nebius connected successfully!');
      setCredentials('');
      setSaServiceAccountId('');
      setSaPublicKeyId('');
      setSaPrivateKey('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectNebius();
      setConnected(false);
      setSuccessMsg('Nebius disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testNebius();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Nebius (${data.project_count ?? 0} project${data.project_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}

      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={nebiusIcon} alt="Nebius" className="w-8 h-8 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Nebius
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>GPU instance lifecycle, managed Kubernetes scaling, and investigation automation</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Region</span>
                <p className="font-medium truncate">{connectedRegion}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Poll Interval</span>
                <p className="font-medium">{formatPollInterval(pollInterval)}</p>
              </div>
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Polling Setup
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Polling active</Badge>
            </CardTitle>
            <CardDescription>Nebius does not send outbound webhooks. Kestrel detects GPU node and instance events by polling the Nebius API on an interval. Follow these steps to set up polling.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg grid sm:grid-cols-2 gap-4 text-sm">
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Poll Interval</p>
                <p className="font-medium">{formatPollInterval(pollInterval)}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Region</p>
                <p className="font-medium truncate">{connectedRegion}</p>
              </div>
            </div>

            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                <span>Create a Nebius <strong className="text-foreground">service account</strong> with read access to your projects and generate API credentials for it.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                <span>Connect those credentials above and select the <strong className="text-foreground">Region</strong> you want Kestrel to monitor.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                <span>Keep the integration connected. Kestrel polls the <strong className="text-foreground">Nebius API</strong> on the interval above and fires the matching <strong className="text-foreground">Nebius trigger</strong> on a GPU error, scheduled maintenance, not-ready node, or stopped instance.</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Nebius actions and triggers in your workflows to automate GPU monitoring and incident response.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'GPU Error', desc: 'Triggers when a node reports a GPU error condition.', Icon: AlertTriangle },
                  { name: 'Maintenance Scheduled', desc: 'Triggers when maintenance is scheduled for a node.', Icon: Activity },
                  { name: 'Node Not Ready', desc: 'Triggers when a node transitions into a not-ready state.', Icon: XCircle },
                  { name: 'Instance Stopped', desc: 'Triggers when an instance transitions into a stopped state.', Icon: StopCircle },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Get Instance', desc: 'Retrieve an instance\u2019s state, config, and region.', Icon: Power },
                  { name: 'Start Instance', desc: 'Start a stopped instance.', Icon: PlayCircle },
                  { name: 'Stop Instance', desc: 'Gracefully stop a running instance.', Icon: StopCircle },
                  { name: 'Restart Instance', desc: 'Restart an instance in place without recreating it.', Icon: RotateCcw },
                  { name: 'List Instances', desc: 'List instances for a project.', Icon: List },
                  { name: 'List Clusters', desc: 'List managed Kubernetes clusters for a project.', Icon: Server },
                  { name: 'List Node Groups', desc: 'List node groups for a managed Kubernetes cluster.', Icon: List },
                  { name: 'Scale Node Group', desc: 'Scale a node group to a target size.', Icon: Maximize2 },
                  { name: 'Create Instance', desc: 'Provision a new GPU or CPU instance with its boot disk.', Icon: PlusCircle },
                  { name: 'Delete Instance', desc: 'Delete an instance and optionally its boot disk.', Icon: Trash2 },
                  { name: 'Create Node Group', desc: 'Provision a new GPU/CPU worker pool in a managed Kubernetes cluster.', Icon: PlusCircle },
                  { name: 'Delete Node Group', desc: 'Delete a node group, tearing down its nodes.', Icon: Trash2 },
                  { name: 'Investigate Nebius', desc: 'AI investigates GPU errors, node failures, and configuration issues.', Icon: Search },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a GPU error is detected on a node in the{' '}
              <input
                value={exampleProject}
                onChange={e => setExampleProject(e.target.value)}
                placeholder="project"
                style={{ width: `${Math.max(exampleProject.length || 7, 7)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              project, investigate the root cause, DM the{' '}
              <input
                value={exampleDeveloper}
                onChange={e => setExampleDeveloper(e.target.value)}
                placeholder="developer"
                style={{ width: `${Math.max(exampleDeveloper.length || 9, 9)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              on Slack with the summary, and create a Jira bug ticket.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleProject || !exampleDeveloper}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a GPU error is detected on a node in the ${exampleProject} project, investigate the root cause, DM ${exampleDeveloper} on Slack with the summary, and create a Jira bug ticket.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={nebiusIcon} alt="Nebius" className="w-8 h-8 object-contain" />
              <div>
                <CardTitle>Connect Nebius</CardTitle>
                <CardDescription>Connect your Nebius AI Cloud account to enable GPU instance lifecycle monitoring, managed Kubernetes scaling, and investigation automation in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Service Account Authorized Key</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates as a Nebius service account using an <strong className="text-foreground">authorized key</strong> (an RSA key pair). The resulting JSON is exchanged for a short-lived IAM token against <strong className="text-foreground">https://api.nebius.cloud</strong> to read instance and node state and perform actions across your projects. Generate it with the CLI or the Nebius console.
              </p>
              <div className="inline-flex rounded-md border border-input bg-background p-0.5 mb-3">
                <button
                  type="button"
                  onClick={() => setCredentialTab('cli')}
                  className={`px-3 py-1.5 text-sm rounded-[5px] transition-colors ${
                    credentialTab === 'cli' ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  CLI
                </button>
                <button
                  type="button"
                  onClick={() => setCredentialTab('ui')}
                  className={`px-3 py-1.5 text-sm rounded-[5px] transition-colors ${
                    credentialTab === 'ui' ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Nebius Console
                </button>
              </div>

              {credentialTab === 'cli' ? (
                <div className="rounded-md border border-input bg-muted/40 p-3 mb-3 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Generate an authorized key for your service account and write it to a file:
                  </p>
                  <pre className="text-xs font-mono bg-background border border-input rounded px-3 py-2 overflow-x-auto whitespace-pre">{`nebius iam auth-public-key generate \\
  --service-account-id <your-service-account-id> \\
  --output authorized-key.json`}</pre>
                  <p className="text-xs text-muted-foreground">
                    Then open <code className="bg-background px-1 rounded">authorized-key.json</code> and paste its full contents below.
                  </p>
                </div>
              ) : (
                <div className="rounded-md border border-input bg-muted/40 p-4 mb-3">
                  <ol className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                      <span>In the <strong className="text-foreground">Nebius console</strong>, go to <strong className="text-foreground">Administration → IAM → Service accounts</strong> and open (or create) the service account Kestrel should use. Note its <strong className="text-foreground">service account ID</strong> (e.g. <code className="bg-background px-1 rounded">serviceaccount-…</code>).</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                      <span>Open the <strong className="text-foreground">Keys</strong> tab and select <strong className="text-foreground">Authorized keys</strong> (not Access keys — those are S3-style credentials Kestrel does not use). Click <strong className="text-foreground">Upload authorized key</strong>.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                      <span>Generate an RSA key pair with the <code className="bg-background px-1 rounded">openssl</code> command Nebius shows in the dialog, keeping <code className="bg-background px-1 rounded">private.pem</code> private.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">4</span>
                      <span><strong className="text-foreground">Attach</strong> the generated <code className="bg-background px-1 rounded">public.pem</code> file, set an <strong className="text-foreground">expiration date</strong> (we recommend <strong className="text-foreground">1 year out</strong> — long enough to avoid breakage, with a reminder to rotate), then click <strong className="text-foreground">Upload key</strong>.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">5</span>
                      <span>Nebius lists the new key with a <strong className="text-foreground">public key ID</strong> (e.g. <code className="bg-background px-1 rounded">publickey-…</code>). Enter the service account ID, public key ID, and the contents of your <code className="bg-background px-1 rounded">private.pem</code> in the fields below — Kestrel assembles them into the credentials for you.</span>
                    </li>
                  </ol>
                </div>
              )}

              {credentialTab === 'cli' ? (
                <>
                  <textarea
                    id="nebius-credentials"
                    value={credentials}
                    onChange={e => setCredentials(e.target.value)}
                    placeholder='{"subject-credentials": {"alg": "RS256", "private-key": "...", "kid": "...", "iss": "...", "sub": "..."}}'
                    rows={6}
                    className="w-full block rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste the full contents of <code className="bg-background px-1 rounded">authorized-key.json</code>. It is stored encrypted and only used to mint short-lived IAM tokens.
                  </p>
                </>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="nebius-sa-id" className="text-sm">Service Account ID</Label>
                    <Input
                      id="nebius-sa-id"
                      type="text"
                      value={saServiceAccountId}
                      onChange={e => setSaServiceAccountId(e.target.value)}
                      placeholder="serviceaccount-…"
                      className="mt-1 font-mono"
                    />
                  </div>
                  <div>
                    <Label htmlFor="nebius-pk-id" className="text-sm">Public Key ID</Label>
                    <Input
                      id="nebius-pk-id"
                      type="text"
                      value={saPublicKeyId}
                      onChange={e => setSaPublicKeyId(e.target.value)}
                      placeholder="publickey-…"
                      className="mt-1 font-mono"
                    />
                  </div>
                  <div>
                    <Label htmlFor="nebius-priv-key" className="text-sm">Private Key (PEM)</Label>
                    <textarea
                      id="nebius-priv-key"
                      value={saPrivateKey}
                      onChange={e => setSaPrivateKey(e.target.value)}
                      placeholder={'-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----'}
                      rows={6}
                      className="mt-1 w-full block rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Paste the full contents of your <code className="bg-background px-1 rounded">private.pem</code>, including the BEGIN/END lines. It is stored encrypted and only used to mint short-lived IAM tokens.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="nebius-poll" className="text-sm">Poll Interval</Label>
              <select
                id="nebius-poll"
                value={connectPollInterval}
                onChange={e => setConnectPollInterval(Number(e.target.value))}
                className="mt-1 w-full sm:w-auto block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {POLL_INTERVAL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Unlike most integrations, Nebius doesn't send outbound webhooks, so Kestrel polls the Nebius API to detect GPU node and instance events. Faster intervals detect incidents sooner; the minimum is 60 seconds.
              </p>
            </div>
            <div>
              <Label htmlFor="nebius-region" className="text-sm">Region</Label>
              <select
                id="nebius-region"
                value={region}
                onChange={e => setRegion(e.target.value)}
                className="mt-1 w-full sm:w-auto block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {REGION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                The Nebius region whose resources Kestrel should manage. A Nebius project is tied to a single region, so pick the region of the project Kestrel will monitor. Defaults to <code className="bg-background px-1 rounded">eu-north1</code>.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !canConnect}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Nebius
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NebiusIntegration;
